import { Typography, Row, Col, Empty } from 'antd';
import { motion, AnimatePresence } from 'motion/react';
import styled from '@emotion/styled';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import { WorkflowDTOType } from '../../../../schemas';
import { useGetQCPendingItemsByTagInfiniteQuery } from '../../../../store/workflows/workflowApi';
import WorkflowItemVertical from './WorkflowItemVertical';
import WorkflowItemVerticalSkeleton from './WorkflowItemVerticalSkeleton';

const Wrapper = styled(motion.div)`
  padding: 16px;
`;

const MotionCol = motion.create(Col);

interface IItemsByTagProps {
  workflow: WorkflowDTOType;
  tags: Array<string>;
}

const ItemsByTag = ({ workflow, tags }: IItemsByTagProps) => {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetQCPendingItemsByTagInfiniteQuery(
      {
        workflowId: workflow.id,
        runId: workflow.currentWorkflowRunId,
        tags,
      },
      {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
      },
    );

  const items = data?.pages.flat() || [];
  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  return (
    <Wrapper initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      {isLoading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} md={8} lg={6}>
                <WorkflowItemVerticalSkeleton />
              </Col>
            ))}
          </Row>
        </motion.div>
      ) : isError ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Typography.Text type="danger">
            Error loading items for {tags.join(', ')}.
          </Typography.Text>
        </motion.div>
      ) : items.length > 0 ? (
        <Row gutter={[16, 32]}>
          <AnimatePresence mode="popLayout">
            {items.map((item, idx) => (
              <MotionCol
                key={item.id}
                xs={24}
                sm={12}
                md={8}
                lg={6}
                layout
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.8,
                  transition: {
                    duration: 0.25,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  },
                }}
                transition={{
                  duration: 0.3,
                  delay: idx < 8 ? idx * 0.05 : 0,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  layout: { duration: 0.2 },
                }}
                style={{
                  transformOrigin: 'center',
                  width: '100%',
                }}
              >
                <WorkflowItemVertical
                  data={item}
                  lastItemRef={idx === items.length - 1 ? lastItemRef : undefined}
                />
              </MotionCol>
            ))}
          </AnimatePresence>
        </Row>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Empty description={`No items found for ${tags.join(',')}.`} />
        </motion.div>
      )}

      {isFetchingNextPage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} md={8} lg={6}>
                <WorkflowItemVerticalSkeleton />
              </Col>
            ))}
          </Row>
        </motion.div>
      )}
    </Wrapper>
  );
};

export default ItemsByTag;
